<template>
  <div class="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-8">
    <div class="mb-8 text-center">
      <h2 class="text-2xl font-bold text-slate-900 mb-2">Completar Configuración</h2>
      <p class="text-slate-500 max-w-lg mx-auto">
        Para comenzar a facturar electrónicamente, necesitamos completar estos pasos esenciales.
      </p>
    </div>

    <div class="max-w-4xl mx-auto">
      <div class="relative after:absolute after:inset-x-0 after:top-1/2 after:block after:h-0.5 after:-translate-y-1/2 after:rounded-lg after:bg-slate-100">
        <ol class="relative z-10 flex justify-between text-sm font-medium text-slate-500">
          
          <!-- Step 1: Registrar Empresa -->
          <li class="flex items-center gap-2 bg-white p-2">
            <span :class="cn(
              'h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors',
              hasCompany 
                ? 'bg-emerald-100 border-emerald-500 text-emerald-700' 
                : 'bg-blue-100 border-blue-500 text-blue-700'
            )">
              <Check v-if="hasCompany" class="h-5 w-5" />
              <span v-else>1</span>
            </span>
            <span :class="cn(hasCompany ? 'text-emerald-700 font-bold' : 'text-slate-900 font-bold')">
              Registrar Empresa
            </span>
          </li>

          <!-- Step 2: Configurar Firma -->
          <li class="flex items-center gap-2 bg-white p-2">
            <span :class="cn(
              'h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors',
              isConfigured
                ? 'bg-emerald-100 border-emerald-500 text-emerald-700' 
                : (hasCompany ? 'bg-blue-100 border-blue-500 text-blue-700' : 'bg-slate-50 border-slate-200 text-slate-400')
            )">
              <Check v-if="isConfigured" class="h-5 w-5" />
              <span v-else>2</span>
            </span>
            <span :class="cn(
              isConfigured 
                ? 'text-emerald-700 font-bold' 
                : (hasCompany ? 'text-slate-900 font-bold' : 'text-slate-400')
            )">
              Firma Electrónica
            </span>
          </li>

          <!-- Step 3: ¡Listo! -->
          <li class="flex items-center gap-2 bg-white p-2">
             <span :class="cn(
              'h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors',
               isConfigured
                ? 'bg-emerald-100 border-emerald-500 text-emerald-700' 
                : 'bg-slate-50 border-slate-200 text-slate-400'
            )">
              <Rocket v-if="isConfigured" class="h-5 w-5" />
              <span v-else>3</span>
            </span>
            <span :class="cn(isConfigured ? 'text-emerald-700 font-bold' : 'text-slate-400')">
              ¡Listo!
            </span>
          </li>
        </ol>
      </div>

      <!-- Action Card -->
      <div class="mt-8 grid gap-6 sm:grid-cols-2">
        <!-- Card 1: Create Company -->
        <div :class="cn(
          'relative overflow-hidden rounded-xl border p-6 transition-all',
          hasCompany ? 'bg-slate-50 border-slate-200 opacity-60' : 'bg-blue-50/50 border-blue-200 shadow-sm hover:shadow-md'
        )">
          <div class="flex items-center gap-4 mb-4">
            <div :class="cn('p-3 rounded-lg', hasCompany ? 'bg-slate-100' : 'bg-blue-100')">
              <Building2 :class="cn('h-6 w-6', hasCompany ? 'text-slate-500' : 'text-blue-600')" />
            </div>
            <div>
              <h3 :class="cn('font-bold', hasCompany ? 'text-slate-700' : 'text-blue-900')">Empresa / RUC</h3>
              <p class="text-xs text-slate-500" v-if="hasCompany">Completado</p>
              <p class="text-xs text-blue-600" v-else>Paso actual</p>
            </div>
          </div>
          <p class="text-sm text-slate-600 mb-4 h-10">
            Registra tu RUC y datos fiscales para emitir comprobantes válidos.
          </p>
          <Button 
            v-if="!hasCompany"
            @click="$router.push('/companies')" 
            class="w-full bg-blue-600 hover:bg-blue-700 text-white"
          >
            Registrar Empresa
            <ArrowRight class="ml-2 h-4 w-4" />
          </Button>
           <Button 
            v-else
            variant="outline"
            class="w-full bg-transparent border-slate-300 text-slate-500 cursor-default"
          >
            Registrado
            <Check class="ml-2 h-4 w-4" />
          </Button>
        </div>

        <!-- Card 2: Configure Signature -->
        <div :class="cn(
          'relative overflow-hidden rounded-xl border p-6 transition-all',
          !hasCompany ? 'opacity-50 pointer-events-none' : (isConfigured ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-orange-200 shadow-sm ring-1 ring-orange-100')
        )">
          <div class="flex items-center gap-4 mb-4">
            <div :class="cn('p-3 rounded-lg', isConfigured ? 'bg-emerald-100' : 'bg-orange-100')">
               <FileSignature :class="cn('h-6 w-6', isConfigured ? 'text-emerald-600' : 'text-orange-600')" />
            </div>
             <div>
              <h3 :class="cn('font-bold', isConfigured ? 'text-emerald-900' : 'text-slate-900')">Firma Electrónica</h3>
              <p class="text-xs text-emerald-600" v-if="isConfigured">Configurado correctamente</p>
              <p class="text-xs text-orange-600 font-medium" v-else>Pendiente</p>
            </div>
          </div>
           <p class="text-sm text-slate-600 mb-4 h-10">
            Sube tu archivo .p12 para firmar automáticamente tus facturas.
          </p>
          <Button 
            v-if="!isConfigured"
            @click="goToSettings"
             class="w-full bg-orange-500 hover:bg-orange-600 text-white"
          >
            Configurar Firma
             <ArrowRight class="ml-2 h-4 w-4" />
          </Button>
           <Button 
            v-else
             variant="outline"
            class="w-full bg-transparent border-emerald-300 text-emerald-600 cursor-default"
          >
            Configurado
             <Check class="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { Check, Building2, FileSignature, Rocket, ArrowRight } from 'lucide-vue-next'
import { cn } from '@/lib/utils'
import Button from '@/components/ui/Button.vue'

const props = defineProps({
  companiesCount: {
    type: Number,
    default: 0
  },
  isConfigured: {
    type: Boolean,
    default: false
  },
  currentCompanyId: {
    type: [Number, String],
    default: null
  }
})

const router = useRouter()

const hasCompany = computed(() => props.companiesCount > 0)

const goToSettings = () => {
    if (props.currentCompanyId) {
        router.push(`/companies/${props.currentCompanyId}/billing/settings`)
    } else {
        router.push('/companies')
    }
}
</script>
